// web/save.ts — local persistence (localStorage). A save is tiny: the world
// inputs (seed + params), the current tick, and the ordered gene-action log.
// Loading rebuilds + replays (see game.ts), so we never serialize the SoA store.
//
// All localStorage access is guarded — private-mode / disabled storage / quota
// errors degrade to "no save" instead of throwing.

import type { WorldParams, PlayerAction } from './game.ts';
import type { HistoryEvent } from './history.ts';
import type { ExplorationSave } from './fog.ts';
import { SPEED_TIERS } from '../src/sim/colonyExample.ts';

/** The valid playback multipliers (the SPEED_TIERS values), for sanitising settings. */
const SPEED_VALUES: readonly number[] = Object.values(SPEED_TIERS);

// v2: power-targeted enemy scaling (src/sim/enemyScaling.ts) changes how an action log
// replays, so v1 saves would mis-replay. Bumping the version rejects them gracefully
// (loadSave returns null ⇒ a fresh start) instead of silently diverging.
export const SAVE_VERSION = 2;
const SAVE_KEY = 'brood.save.v1';
const SETTINGS_KEY = 'brood.settings.v1';

export interface SaveState {
  version: number;
  name: string;
  createdAt: number;
  savedAt: number;
  params: WorldParams;
  tick: number;
  actions: PlayerAction[];
  /** Integrity fingerprint captured at save time; re-checked after replay. */
  hash: string;
  /** The colony's lineage logbook (observed events; restored verbatim, not replayed). */
  history?: HistoryEvent[];
  /**
   * Fog-of-war exploration memory (which tiles the player has discovered). A
   * compact RLE blob restored verbatim — NOT part of the deterministic replay, so
   * it never affects the integrity hash. Absent on legacy saves (→ starts unexplored).
   */
  explored?: ExplorationSave;
}

export interface Settings {
  /** Base simulation ticks per second at 1× speed. */
  ticksPerSecond: number;
  /** Playback speed tier (SPEED_TIERS): 0.5 observation / 1 intended / 2 / 5. */
  speedMultiplier: number;
  showHiveNodes: boolean;
  showGrid: boolean;
  /** Pheromone trail heatmap + courier motes (the logistics network view). */
  showTrails: boolean;
  /** Colour organisms by generation (the evolution-propagation overlay). */
  showGenerations: boolean;
  /** Master SFX volume, 0..1 (audio is a presentation pref — never in the save replay). */
  masterVolume: number;
  /** Mute all SFX. */
  muted: boolean;
  autosave: boolean;
  /** New-colony world size + rival count (applied on New Colony only). */
  width: number;
  height: number;
  colonies: number;
}

export const DEFAULT_SETTINGS: Settings = {
  // MOVEMENT/PACING: base rate 20 → 12 (pacing) → 6 (movement audit) so 1× motion is
  // ~25% as fast as the original and reads calm + biological. The sim still steps 1
  // tile/tick (the determinism quantum — unslowable in-sim); the rate is the real
  // lever, and web/watch.ts adds the per-caste inertia/turn-limits/idle that make a
  // slow colony feel deliberate. SPEED_TIERS scale from here: 0.5× study … 5× manage.
  ticksPerSecond: 6,
  speedMultiplier: 1,
  showHiveNodes: true,
  showGrid: false,
  showTrails: true,
  showGenerations: false,
  masterVolume: 0.5,
  muted: false,
  autosave: true,
  // WORLD-SCALE fallback for New Colony / saves missing dims — matches
  // DEFAULT_PARAMS in web/game.ts (the ~7.4x-area world). Saved games keep their
  // own recorded dims, so this only seeds fresh colonies and legacy saves.
  width: 150,
  height: 88,
  colonies: 4,
};

function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    // Probe — some environments expose the object but throw on access.
    const k = '__brood_probe__';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    return null;
  }
}

export function hasSave(): boolean {
  const s = storage();
  if (!s) return false;
  return s.getItem(SAVE_KEY) !== null;
}

export function loadSave(): SaveState | null {
  const s = storage();
  if (!s) return null;
  const raw = s.getItem(SAVE_KEY);
  if (raw === null) return null;
  try {
    const data = JSON.parse(raw) as SaveState;
    if (typeof data !== 'object' || data === null) return null;
    if (data.version !== SAVE_VERSION) return null;
    if (!data.params || !Array.isArray(data.actions)) return null;
    return data;
  } catch {
    return null;
  }
}

/** Persist a save. Returns true on success. */
export function writeSave(save: SaveState): boolean {
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(SAVE_KEY, JSON.stringify(save));
    return true;
  } catch {
    return false;
  }
}

export function clearSave(): void {
  const s = storage();
  if (!s) return;
  try { s.removeItem(SAVE_KEY); } catch { /* ignore */ }
}

export function loadSettings(): Settings {
  const s = storage();
  if (!s) return { ...DEFAULT_SETTINGS };
  const raw = s.getItem(SETTINGS_KEY);
  if (raw === null) return { ...DEFAULT_SETTINGS };
  try {
    const parsed = JSON.parse(raw) as Partial<Settings>;
    const merged = { ...DEFAULT_SETTINGS, ...parsed };
    // Sanitise the playback multiplier against the canonical SPEED_TIERS set so a
    // legacy / malformed persisted value can't leak in (a valid 0.5× round-trips).
    if (!SPEED_VALUES.includes(merged.speedMultiplier)) {
      merged.speedMultiplier = DEFAULT_SETTINGS.speedMultiplier;
    }
    return merged;
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function writeSettings(settings: Settings): void {
  const s = storage();
  if (!s) return;
  try { s.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch { /* ignore */ }
}
